/**
 * Demo mode only: the mock action handler accepts project.add and project.remove but does not
 * change the state. These apply what a real agent would report next, so the demo shows the
 * result. Never used against a hub.
 */
import { repoNameOf, suggestionKey } from '@orgus/protocol';
import type { Checkout, Id, Project } from '@orgus/protocol';
import { useOrgus } from '../../lib/store.ts';

function idFor(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0).toString(16).padStart(8, '0');
}

export function mockProjectAdded(machineId: Id, path: string): void {
  const st = useOrgus.getState();
  if (st.mode !== 'mock') return;
  const now = Date.now();
  const suggestion = Object.values(st.suggestions).find((s) => s.machineId === machineId && s.path === path);
  const name = suggestion?.name ?? repoNameOf(path);
  const remoteUrl = suggestion?.remoteUrl ?? null;
  const existing = Object.values(st.projects).find((p) => (remoteUrl && p.remoteUrl === remoteUrl) || p.name.toLowerCase() === name.toLowerCase());
  const project: Project = existing ?? { id: `p-${idFor(remoteUrl ?? name)}`, name, remoteUrl, defaultBranch: 'main', adapters: ['generic-git'] };
  const checkout: Checkout = {
    id: `c-${idFor(`${machineId}:${path}`)}`,
    projectId: project.id,
    machineId,
    path,
    kind: 'main',
    branch: 'main',
    head: idFor(path).repeat(5),
    headSubject: 'Latest commit on main',
    ahead: 0,
    behind: 0,
    dirty: 0,
    updatedAt: now,
  };
  st.applyPatch({
    at: now,
    upserts: { projects: existing ? [] : [project], checkouts: [checkout] },
    removes: suggestion ? { suggestions: [suggestionKey(suggestion)] } : {},
  });
}

export function mockProjectRemoved(projectId: Id, machineId: Id): void {
  const st = useOrgus.getState();
  if (st.mode !== 'mock') return;
  const checkouts = Object.values(st.checkouts).filter((c) => c.projectId === projectId && c.machineId === machineId);
  const elsewhere = Object.values(st.checkouts).some((c) => c.projectId === projectId && c.machineId !== machineId);
  st.applyPatch({
    at: Date.now(),
    upserts: {},
    removes: { checkouts: checkouts.map((c) => c.id), projects: elsewhere ? [] : [projectId] },
  });
}
